export interface MidiMessage {
  status: number;
  note: number;
  velocity: number;
}

// In-memory subscribers for virtual (on-screen) MIDI sources such as the
// draggable piano keyboard easter egg. We keep this here so that anything
// using subscribeToMidi will automatically receive both hardware and virtual
// messages without needing any extra wiring.
const virtualSubscribers = new Set<(msg: MidiMessage) => void>();

export function sendVirtualMidiMessage(msg: MidiMessage): void {
  virtualSubscribers.forEach((listener) => {
    try {
      listener(msg);
    } catch (err) {
      console.error('[WebMIDI] Error in virtual MIDI listener:', err);
    }
  });
}

// Minimal Web MIDI types to avoid bringing in external typings.
// These are intentionally partial and only cover what we use here.
interface MidiMessageEvent {
  data: Uint8Array;
}

interface MidiInput {
  addEventListener(
    type: 'midimessage',
    listener: (event: MidiMessageEvent) => void,
    options?: boolean | AddEventListenerOptions
  ): void;
  removeEventListener(
    type: 'midimessage',
    listener: (event: MidiMessageEvent) => void,
    options?: boolean | EventListenerOptions
  ): void;
}

interface MidiAccess {
  inputs: Iterable<MidiInput> & { forEach(callback: (input: MidiInput) => void): void };
  onstatechange: ((event: Event) => void) | null;
}

/**
 * Subscribes to Web MIDI messages (if supported) and calls the provided callback
 * with normalized { status, note, velocity } objects.
 *
 * Returns an unsubscribe function that removes all listeners (both hardware
 * and virtual).
 *
 * This is intentionally defensive: it no-ops on unsupported browsers or
 * permission errors so the rest of the app can continue to function.
 */
export function subscribeToMidi(onMessage: (msg: MidiMessage) => void): () => void {
  if (typeof window === 'undefined') {
    // Server-side render / non-browser environment
    return () => {};
  }

  // Register this callback for virtual MIDI sources immediately so things like
  // the on-screen keyboard can work even if hardware MIDI is unavailable.
  virtualSubscribers.add(onMessage);

  const anyNavigator = navigator as Navigator & {
    requestMIDIAccess?: (options?: { sysex: boolean }) => Promise<MidiAccess>;
  };

  if (typeof anyNavigator.requestMIDIAccess !== 'function') {
    console.warn('[WebMIDI] Browser does not support Web MIDI API.');
    return () => {
      virtualSubscribers.delete(onMessage);
    };
  }

  let isActive = true;
  const inputs: MidiInput[] = [];

  const handleMidiMessage = (event: MidiMessageEvent) => {
    if (!isActive) return;
    if (!event.data || event.data.length === 0) return;

    const [status, note, velocity = 0] = event.data;

    // Basic sanity checks
    if (typeof status !== 'number' || typeof note !== 'number') return;

    try {
      onMessage({ status, note, velocity });
    } catch (err) {
      console.error('[WebMIDI] Error in MIDI callback:', err);
    }
  };

  anyNavigator
    .requestMIDIAccess({ sysex: false })
    .then((access) => {
      if (!isActive) return;

      const attachListeners = () => {
        access.inputs.forEach((input: MidiInput) => {
          if (!inputs.includes(input)) {
            input.addEventListener('midimessage', handleMidiMessage);
            inputs.push(input);
          }
        });
      };

      attachListeners();

      access.onstatechange = () => {
        if (!isActive) return;
        // Reattach listeners if devices change
        inputs.forEach((input) => {
          input.removeEventListener('midimessage', handleMidiMessage);
        });
        inputs.length = 0;
        attachListeners();
      };
    })
    .catch((err) => {
      console.warn('[WebMIDI] Failed to get MIDI access:', err);
    });

  return () => {
    isActive = false;
    inputs.forEach((input) => {
      input.removeEventListener('midimessage', handleMidiMessage);
    });
    inputs.length = 0;
    virtualSubscribers.delete(onMessage);
  };
}


